/**
 * Routes from OSRM, the OpenStreetMap router, as served by FOSSGIS for the
 * OSM community: by car on roads, on foot along paths and pavements, by bike
 * on paths and roads — which is where a motocross bike goes too.
 *
 * A car's route is then looked up in OpenStreetMap through Overpass: which
 * roads it runs along, with their `maxspeed` and `highway` class. That gives
 * the speed to drive — the real limit — and at each turn whether to stop,
 * give way, or go straight on (see `core/nav/roads.ts`). Without Overpass the
 * route still drives, on the router's own speeds.
 *
 * Keyless, with CORS. Asked once, when a destination is chosen: the start and
 * the end are sent to the router, and the route's node numbers to Overpass.
 */

import type { RoutePoint } from '../core/nav/route.js';
import { giveAt, limitFromRouter, limitOf, roadRank } from '../core/nav/roads.js';

export type Profile = 'car' | 'foot' | 'bike';

const ENDPOINT: Record<Profile, string> = {
  car: 'https://routing.openstreetmap.de/routed-car/route/v1/driving',
  foot: 'https://routing.openstreetmap.de/routed-foot/route/v1/driving',
  bike: 'https://routing.openstreetmap.de/routed-bike/route/v1/driving',
};

const OVERPASS = [
  'https://overpass-api.de/api/interpreter',
  'https://overpass.private.coffee/api/interpreter',
  'https://maps.mail.ru/osm/tools/overpass/api/interpreter',
];
/** How long each server may take over the road lookup before the next is asked, ms. */
const ROADS_TIMEOUT = 15_000;

export interface FetchedRoute {
  points: RoutePoint[];
  /** The speed limit per stretch between points, m/s: OSM's, else implied by the router's speed. NaN where neither says. */
  speeds: number[];
  /** Where the route turns and must stop at the line, or slow and give way. */
  junctions: Array<{ lat: number; lon: number; stop: boolean }>;
  /** Metres, and seconds as the router reckons them. */
  distance: number;
  duration: number;
  /** Whether the limits are the roads' own, from OSM, rather than read back from the router's speeds. */
  limits: boolean;
  /** Whether it crosses water on a ferry somewhere. */
  ferry: boolean;
  /**
   * The same with the roads' own limits and ranks, from OSM through Overpass,
   * when it answers: undefined when it does not.
   */
  roads: Promise<{ speeds: number[]; junctions: FetchedRoute['junctions'] } | undefined>;
}

interface OsrmStep {
  mode?: string;
  maneuver: { type: string; modifier?: string; location: [number, number] };
}

export async function fetchRoute(profile: Profile, from: RoutePoint, to: RoutePoint, signal?: AbortSignal): Promise<FetchedRoute> {
  const url = `${ENDPOINT[profile]}/${from.lon.toFixed(6)},${from.lat.toFixed(6)};${to.lon.toFixed(6)},${to.lat.toFixed(6)}` +
    '?overview=full&geometries=geojson&steps=true&annotations=true';
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`The router answered ${response.status}`);
  const body = await response.json() as {
    code: string; message?: string;
    routes?: Array<{
      distance: number; duration: number;
      geometry: { coordinates: Array<[number, number]> };
      legs: Array<{ steps: OsrmStep[]; annotation?: { speed?: number[]; nodes?: number[] } }>;
    }>;
  };
  const route = body.routes?.[0];
  if (body.code !== 'Ok' || route === undefined) throw new Error(body.message ?? 'No way there was found');
  const coords = route.geometry.coordinates;
  const points = coords.map(([lon, lat]) => ({ lat, lon }));
  const estimates = route.legs.flatMap(l => l.annotation?.speed ?? []);
  const nodes = route.legs.flatMap((l, i) => (i === 0 ? l.annotation?.nodes ?? [] : (l.annotation?.nodes ?? []).slice(1)));
  const steps = route.legs.flatMap(l => l.steps);
  // Where each maneuver is on the line, to read the roads either side of it.
  const index = new Map<string, number>();
  coords.forEach(([lon, lat], i) => { const k = `${lon.toFixed(6)},${lat.toFixed(6)}`; if (!index.has(k)) index.set(k, i); });

  /** Limits and junctions, from the roads' ranks and limits when known. */
  const plan = (roads: ReadonlyArray<{ rank: number; limit: number | undefined }> | undefined) => {
    // The limit on each stretch: OSM's own, else read back from the router's speed.
    const speeds = estimates.map((v, i) => roads?.[i]?.limit ?? limitFromRouter(v) ?? NaN);
    const junctions: FetchedRoute['junctions'] = [];
    for (const step of steps) {
      const { type, modifier, location } = step.maneuver;
      const i = index.get(`${location[0].toFixed(6)},${location[1].toFixed(6)}`);
      const a = roads !== undefined && i !== undefined && i > 0 ? roads[i - 1]?.rank ?? 0 : 0;
      const b = roads !== undefined && i !== undefined ? roads[i]?.rank ?? 0 : 0;
      const give = giveAt(type, modifier, a, b);
      if (give !== 'none') junctions.push({ lat: location[1], lon: location[0], stop: give === 'stop' });
    }
    return { speeds, junctions };
  };

  const first = plan(undefined);
  return {
    points, ...first, distance: route.distance, duration: route.duration, limits: false,
    ferry: steps.some(s => s.mode === 'ferry'),
    // The roads themselves, asked of OSM afterwards: the trip need not wait for them.
    roads: profile === 'car' && nodes.length === coords.length
      ? roadsAlong(nodes, signal).then(plan).catch(() => undefined)
      : Promise.resolve(undefined),
  };
}

/**
 * For each stretch between consecutive route nodes, the road it is on: its
 * rank and its limit, m/s. From Overpass, the ways through those nodes.
 */
async function roadsAlong(nodes: readonly number[], signal?: AbortSignal): Promise<Array<{ rank: number; limit: number | undefined }>> {
  const ids = [...new Set(nodes)].join(',');
  const query = `[out:json][timeout:20];node(id:${ids});way(bn)["highway"];out body;`;
  let ways: Array<{ nodes: number[]; tags: Record<string, string> }> | undefined;
  for (const endpoint of OVERPASS) {
    const timer = new AbortController();
    const stop = setTimeout(() => timer.abort(), ROADS_TIMEOUT);
    const onAbort = () => timer.abort();
    signal?.addEventListener('abort', onAbort, { once: true });
    try {
      const r = await fetch(endpoint, { method: 'POST', body: new URLSearchParams({ data: query }), signal: timer.signal });
      if (!r.ok) continue;
      const json = await r.json() as { elements: Array<{ type: string; nodes?: number[]; tags?: Record<string, string> }> };
      ways = json.elements.filter(e => e.type === 'way' && e.nodes !== undefined)
        .map(e => ({ nodes: e.nodes!, tags: e.tags ?? {} }));
      break;
    } catch {
      if (signal?.aborted) throw new Error('Aborted');
    } finally {
      clearTimeout(stop);
      signal?.removeEventListener('abort', onAbort);
    }
  }
  if (ways === undefined) throw new Error('No road data');
  // Which way each pair of neighbouring nodes lies on.
  const byPair = new Map<string, Record<string, string>>();
  for (const w of ways) {
    for (let k = 0; k + 1 < w.nodes.length; k++) {
      byPair.set(`${w.nodes[k]}-${w.nodes[k + 1]}`, w.tags);
      byPair.set(`${w.nodes[k + 1]}-${w.nodes[k]}`, w.tags);
    }
  }
  const out: Array<{ rank: number; limit: number | undefined }> = [];
  for (let i = 0; i + 1 < nodes.length; i++) {
    const tags = byPair.get(`${nodes[i]}-${nodes[i + 1]}`);
    out.push({ rank: roadRank(tags?.highway), limit: limitOf(tags?.highway, tags?.maxspeed) });
  }
  return out;
}
